import {type FC, useCallback, useEffect, useState} from 'react';

import {useI18n} from '@yourcoach/i18n';

import type {Props} from './types';

import VideoOnIcon from '../../../../assets/icons/primary/Video 2.svg';
import VideoOffIcon from '../../../../assets/icons/primary/Video off.svg';
import AudioOnIcon from '../../../../assets/icons/primary/Voice 3.svg';
import AudioOffIcon from '../../../../assets/icons/primary/Voice off.svg';
import {
  DevicePermission,
  requestMediaPermissions,
} from '../../../../utils/device-permissions';
import {OptionButton} from './OptionButton';
import * as S from './styles';

export const ConferencePreview: FC<Props> = ({
  isAudioEnabled: isAudioEnabledInitially = true,
  isStart,
  isVideoEnabled: isVideoEnabledInitially = true,
  onJoinButtonClick,
  ...rest
}) => {
  const {t} = useI18n();

  const [isAudioEnabled, setIsAudioEnabled] = useState(
    Boolean(isAudioEnabledInitially),
  );

  const [isVideoEnabled, setIsVideoEnabled] = useState(
    Boolean(isVideoEnabledInitially),
  );

  const onAudioOptionClick = useCallback(() => {
    setIsAudioEnabled(prev => !prev);
  }, []);

  const onVideoOptionClick = useCallback(() => {
    setIsVideoEnabled(prev => !prev);
  }, []);

  const onJoinButtonClickCb = useCallback(() => {
    onJoinButtonClick({isAudioEnabled, isVideoEnabled});
  }, [isAudioEnabled, isVideoEnabled, onJoinButtonClick]);

  useEffect(() => {
    requestMediaPermissions([DevicePermission.MICROPHONE]);
  }, []);

  return (
    <S.Container {...rest}>
      <S.Camera />
      <S.OptionsContainer>
        <OptionButton
          icon={isAudioEnabled ? AudioOnIcon : AudioOffIcon}
          isActive={isAudioEnabled}
          label={
            /**
             * @i18n_parser
             * t('shared:Conference.preview.audio.enabled')
             * t('shared:Conference.preview.audio.disabled')
             */
            t(
              `shared:Conference.preview.audio.${isAudioEnabled ? 'enabled' : 'disabled'}`,
            )
          }
          onClick={onAudioOptionClick}
        />
        <S.OptionsSeparator />
        <OptionButton
          icon={isVideoEnabled ? VideoOnIcon : VideoOffIcon}
          isActive={isVideoEnabled}
          label={
            /**
             * @i18n_parser
             * t('shared:Conference.preview.video.enabled')
             * t('shared:Conference.preview.video.disabled')
             */
            t(
              `shared:Conference.preview.video.${isVideoEnabled ? 'enabled' : 'disabled'}`,
            )
          }
          onClick={onVideoOptionClick}
        />
      </S.OptionsContainer>
      <S.ActionButton onClick={onJoinButtonClickCb}>
        {isStart ? t('shared:start') : t('shared:join')}
      </S.ActionButton>
    </S.Container>
  );
};
